import { AuthorizationType } from "@nova/db-table-integration-authorizations"
import { IntegrationType } from "@nova/schemas"
import type { IntegrationAuthorizationResponse } from "@nova/data-facade"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { FunctionComponent, useState } from "react"
import { invalidateIntegrationAuthorizations } from "features/data-management/integrations/useIntegrations"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useAppDispatch } from "integrations/redux/hooks"
import { ButtonPrimary, ButtonStatus } from "@nova/component-btn"
import { Input } from "@nova/ds-component-input"
import classNames from "classnames"

interface CalTopoIntegrationUIProps {
    authorization: IntegrationAuthorizationResponse<AuthorizationType, IntegrationType.caltopo>
}

const errorByStatus: Record<number, string> = {
    422: "Enter a CalTopo map ID or map URL.",
    502: "These credentials can't access that map.",
}

const CalTopoIntegrationUI: FunctionComponent<CalTopoIntegrationUIProps> = ({ authorization }) => {
    const [map, setMap] = useState(authorization.settings.mapId)
    const [error, setError] = useState<string>()
    const [isMutating, setIsMutating] = useState(false)
    const dispatch = useAppDispatch()

    const handleUpdateClick = async () => {
        setIsMutating(true)
        const res = await fetchApiGateway("patch /org/:orgId/integrations/caltopo/map", {
            params: { orgId: authorization.workspaceId },
            body: { map },
            rawResponse: true,
        }).finally(() => setIsMutating(false))

        if (!res.ok) return setError(errorByStatus[res.status] ?? "Failed to update the map.")

        setError(undefined)
        invalidateIntegrationAuthorizations()
        dispatch(
            setGlobalNotificationContext({
                message: "Your CalTopo map has been updated.",
                status: GlobalNotificationStatus.success,
                timeoutInMs: 3000,
            })
        )
    }

    return (
        <div>
            <h5 className="text-gray-100">Destination Map</h5>
            <div className="flex flex-col gap-1">
                <div className="flex gap-2">
                    <Input
                        className={classNames("max-w-[192px]", {
                            "[&>div]:border-red-500": error,
                        })}
                        value={map}
                        onChange={(e) => setMap(e.target.value)}
                        placeholder="https://caltopo.com/m/ABC123"
                    />
                    <ButtonPrimary
                        onClick={handleUpdateClick}
                        status={isMutating ? ButtonStatus.Loading : ButtonStatus.Normal}
                        title="Update"
                        disabled={!map}
                        height="h-fill"
                    />
                </div>
                {error && <small className="text-red-500">{error}</small>}
            </div>
        </div>
    )
}

export { CalTopoIntegrationUI }
